import { t } from '@lingui/core/macro';
import { X } from '@untitledui/icons/X';

import { Button } from '@/components/base/buttons/button';
import { MultiSelectDropdown } from '@/components/base/select/multi-select-dropdown';

export const PARTICIPANT_STATUS_FILTER_VALUES = [
  'PENDING',
  'INVITED',
  'SCHEDULED',
  'ATTENDED',
  'PAID',
] as const;

export type ParticipantStatusFilter =
  (typeof PARTICIPANT_STATUS_FILTER_VALUES)[number];

// `Record<…, string>` enforces a label for every filter value at compile time.
function getStatusLabels(): Record<ParticipantStatusFilter, string> {
  return {
    PENDING: t`Applied`,
    INVITED: t`Invited`,
    SCHEDULED: t`Scheduled`,
    ATTENDED: t`Attended`,
    PAID: t`Paid`,
  };
}

interface ParticipantStatusFilterProps {
  value: ReadonlySet<ParticipantStatusFilter>;
  onChange: (next: Set<ParticipantStatusFilter>) => void;
}

export function ParticipantStatusFilter({
  value,
  onChange,
}: ParticipantStatusFilterProps) {
  const labels = getStatusLabels();
  const items = PARTICIPANT_STATUS_FILTER_VALUES.map((id) => ({
    id,
    label: labels[id],
  }));
  return (
    <MultiSelectDropdown<ParticipantStatusFilter>
      size="xs"
      items={items}
      selectedKeys={value}
      onSelectionChange={onChange}
      triggerVariant="button"
      showCheckboxes
      placeholder={t`All statuses`}
      triggerPrefix={t`Status`}
      maxDisplay={3}
      noWrap
      matchTriggerWidth={false}
      popoverPlacement="bottom start"
      popoverClassName="min-w-[240px]"
      aria-label={t`Filter by status`}
      footer={({ clearAll, close }) => (
        <Button
          color="tertiary"
          size="xs"
          iconLeading={X}
          className="w-full justify-start gap-2 px-3"
          onPress={() => {
            clearAll();
            close();
          }}
        >
          {t`Clear selection`}
        </Button>
      )}
    />
  );
}
